/**
 * AuthProvider — single source of truth for "who is logged in".
 *
 * On mount, if there's a token in localStorage, fetch /auth/me to confirm
 * it's still valid and populate the user. On login, store token + trigger
 * a me-refresh. On logout, clear token and redirect to /login.
 *
 * `loading` is true during the boot-time /me call so we don't flash the
 * login page for a user with a valid token.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { api, clearToken, getToken, setToken, type User } from "./api";

interface AuthCtx {
  user: User | null;
  loading: boolean;
  /** Returns the authenticated user so the caller can route based on role
   *  (e.g. platform admin → /admin, tenant → /). */
  login: (email: string, password: string) => Promise<User>;
  /** Platform admin login — username + password, not email. Hits
   *  /auth/admin-login which only accepts platform-admin users. */
  adminLogin: (username: string, password: string) => Promise<User>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const refreshUser = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const me = await api.get<User>("/auth/me");
      setUser(me);
    } catch {
      // 401 handler in api.ts already cleared the token; fall through.
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = useCallback(
    async (email: string, password: string): Promise<User> => {
      const resp = await api.post<{ access_token: string }>("/auth/login", {
        email,
        password,
      });
      setToken(resp.access_token);
      // Fetch /auth/me synchronously + set state ourselves. refreshUser's
      // setState is async, so `await login()` used to return before the
      // user prop updated — caller couldn't branch on user.is_platform_admin
      // right after await without a stale read.
      const me = await api.get<User>("/auth/me");
      setUser(me);
      setLoading(false);
      return me;
    },
    [],
  );

  const adminLogin = useCallback(
    async (username: string, password: string): Promise<User> => {
      const resp = await api.post<{ access_token: string }>("/auth/admin-login", {
        username,
        password,
      });
      setToken(resp.access_token);
      const me = await api.get<User>("/auth/me");
      setUser(me);
      setLoading(false);
      return me;
    },
    [],
  );

  const logout = useCallback(() => {
    clearToken();
    setUser(null);
    // Hard redirect so React Router stack resets and any stale API state
    // from the previous session doesn't leak into the next one.
    window.location.href = "/login";
  }, []);

  return (
    <Ctx.Provider value={{ user, loading, login, adminLogin, logout, refreshUser }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth(): AuthCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
